import React, { useCallback, useEffect, useState } from 'react';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { CountdownDisplay } from '../../components/competition/CountdownBanner';
import {
  CompetitionRole, CountdownChanges, CountdownSettings as Settings, ROLE_LABELS, errorMessage, fetchCountdown, saveCountdown,
} from '../../services/manageApi';
import { DEFAULT_COUNTDOWN_LABEL, remainingMs, toLocalInputValue, toOffsetIso } from '../../utils/countdown';
import { formatDateTime, viewerTimeZone } from '../../utils/dates';

interface Props {
  competitionId: number;
  canEdit: boolean;
  requiredRole?: CompetitionRole;
  onDirtyChange: (section: string, dirty: boolean) => void;
}

const SECTION = 'countdown';
const HINT = 'Display only — submissions stay open until a platform admin sets the competition status to Closed.';
const NEEDS_TIME = 'Set a closing time before turning on the countdown';
const INVALID_TIME = 'Enter a valid date and time';
const TOAST_MS = 4000;

/** Readable zone name (e.g. "Central Daylight Time") for the given instant, falling back to the IANA id. */
function zoneName(date: Date): string {
  const iana = Intl.DateTimeFormat().resolvedOptions().timeZone;
  try {
    const part = new Intl.DateTimeFormat(undefined, { timeZoneName: 'long' })
      .formatToParts(date)
      .find((p) => p.type === 'timeZoneName');
    return part?.value || iana;
  } catch {
    return iana;
  }
}

const CountdownSettings: React.FC<Props> = ({ competitionId, canEdit, requiredRole, onDirtyChange }) => {
  const [saved, setSaved] = useState<Settings | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [localTime, setLocalTime] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  /** Returns false when loading failed (the error is shown). */
  const load = useCallback(async (): Promise<boolean> => {
    setError('');
    try {
      const result = await fetchCountdown(competitionId);
      setSaved(result);
      setEnabled(result.enabled);
      setLocalTime(toLocalInputValue(result.ends_at));
      setLabel(result.label ?? '');
      return true;
    } catch (err) {
      setError(errorMessage(err, 'Failed to load countdown settings'));
      return false;
    }
  }, [competitionId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(''), TOAST_MS);
    return () => clearTimeout(timer);
  }, [toast]);

  /** Every edit drops stale feedback from the previous save. */
  const edited = () => {
    setError('');
    setToast('');
  };

  const endsIso = localTime ? toOffsetIso(localTime) : null;
  const invalidTime = Boolean(localTime) && !endsIso;
  const needsTime = enabled && !localTime;
  const savedLocal = toLocalInputValue(saved?.ends_at ?? null);

  const changes: CountdownChanges = {};
  if (saved) {
    const clearing = Boolean(saved.ends_at) && !localTime;
    if (enabled !== saved.enabled && !(clearing && enabled)) changes.countdown_enabled = enabled;
    if (clearing) changes.clear_countdown_ends_at = true;
    else if (endsIso && localTime !== savedLocal) changes.countdown_ends_at = endsIso;
    if (label.trim() !== (saved.label ?? '')) changes.countdown_label = label.trim();
  }
  const dirty = Object.keys(changes).length > 0;
  useEffect(() => { onDirtyChange(SECTION, dirty); }, [dirty, onDirtyChange]);

  const submit = async (body: CountdownChanges) => {
    setBusy(true);
    setError('');
    setToast('');
    try {
      await saveCountdown(competitionId, body);
      // Saved either way; only claim success here if the fresh values could be shown.
      if (await load()) setToast('Countdown saved');
    } catch (err) {
      setError(errorMessage(err, 'Failed to save countdown'));
    } finally {
      setBusy(false);
    }
  };

  const inPast = Boolean(endsIso && Date.parse(endsIso) < Date.now());
  const zoneDate = endsIso ? new Date(endsIso) : new Date();

  return (
    <section className="manage-section" data-tour="manage-countdown">
      <h3>
        Live countdown
        {!canEdit && requiredRole && <span className="manage-field__lock">🔒 {ROLE_LABELS[requiredRole]}</span>}
      </h3>
      <p className="manage-help">{HINT}</p>
      {!saved && !error && <LoadingSpinner size="sm" text="Loading countdown..." />}
      {saved && !canEdit && (
        <p>
          {saved.enabled && saved.ends_at
            ? `On — closes ${formatDateTime(saved.ends_at, '-', viewerTimeZone())}`
            : 'Off'}
          {saved.label ? ` · "${saved.label}"` : ''}
        </p>
      )}
      {saved && canEdit && (
        <div className="manage-form">
          <label className="manage-field">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => { setEnabled(e.target.checked); edited(); }}
              disabled={busy}
            />
            {' '}Show countdown banner
          </label>
          <label className="manage-field" htmlFor="countdown-time">
            <span className="manage-field__label">Closing time</span>
          </label>
          <input
            id="countdown-time"
            type="datetime-local"
            className="input input--default input--md"
            value={localTime}
            onChange={(e) => { setLocalTime(e.target.value); edited(); }}
            disabled={busy}
          />
          <span className="manage-field__note" data-testid="countdown-zone">{zoneName(zoneDate)}</span>
          {invalidTime && <p className="manage-warning">{INVALID_TIME}</p>}
          {needsTime && <p className="manage-warning">{NEEDS_TIME}</p>}
          {inPast && <p className="manage-warning">This time is in the past.</p>}
          {localTime && (
            <Button variant="ghost" size="sm" onClick={() => { setLocalTime(''); edited(); }} disabled={busy}>
              Clear time
            </Button>
          )}
          <label className="manage-field" htmlFor="countdown-label">
            <span className="manage-field__label">Banner text (optional)</span>
          </label>
          <input
            id="countdown-label"
            className="input input--default input--md"
            placeholder={DEFAULT_COUNTDOWN_LABEL}
            maxLength={120}
            value={label}
            onChange={(e) => { setLabel(e.target.value); edited(); }}
            disabled={busy}
          />
          {endsIso && (
            <div className="countdown-settings__preview">
              <span className="manage-field__label">Preview</span>
              <CountdownDisplay
                label={label.trim() || null}
                endsAt={endsIso}
                remaining={remainingMs(endsIso, Date.now(), 0)}
                announce={false}
              />
            </div>
          )}
          {toast && <p className="manage-success" role="status">{toast}</p>}
          <Button
            variant="primary"
            onClick={() => submit(changes)}
            disabled={!dirty || busy || needsTime || invalidTime}
            loading={busy}
          >
            Save countdown
          </Button>
        </div>
      )}
      {error && <p className="manage-error" role="alert">{error}</p>}
      {!saved && error && (
        <Button variant="ghost" size="sm" onClick={() => load()}>
          Retry
        </Button>
      )}
    </section>
  );
};

export default CountdownSettings;
